import { motion } from 'framer-motion';
import { useId } from 'react';
import type { TargetRect } from './useWalkthroughTarget';

interface WalkthroughSpotlightProps {
  /** Alvo a iluminar. `null` escurece a tela inteira (passo tipo modal). */
  rect: TargetRect | null;
  /** Folga em volta do alvo, em px. */
  padding?: number;
  onOverlayClick?: () => void;
}

const SPRING = { type: 'spring' as const, stiffness: 320, damping: 34 };

/**
 * Overlay escuro com recorte iluminado no elemento alvo.
 *
 * Usa uma máscara SVG (retângulo branco cobrindo a tela + retângulo preto no
 * alvo) em vez de quatro divs, para que o recorte tenha cantos arredondados e
 * anime suavemente de um passo para o outro.
 */
export function WalkthroughSpotlight({
  rect,
  padding = 8,
  onOverlayClick,
}: WalkthroughSpotlightProps) {
  const maskId = useId().replace(/:/g, '');

  const hole = rect
    ? {
        x: Math.max(rect.left - padding, 0),
        y: Math.max(rect.top - padding, 0),
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      }
    : null;

  const radius = hole ? Math.min(16, hole.height / 2, hole.width / 2) : 0;

  return (
    <motion.div
      className="fixed inset-0 z-[100]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onOverlayClick}
      aria-hidden="true"
    >
      <svg className="h-full w-full" width="100%" height="100%">
        <defs>
          <mask id={maskId}>
            {/* Branco = escurecido, preto = recortado */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hole && (
              <motion.rect
                initial={false}
                animate={{
                  x: hole.x,
                  y: hole.y,
                  width: hole.width,
                  height: hole.height,
                }}
                transition={SPRING}
                rx={radius}
                ry={radius}
                fill="black"
              />
            )}
          </mask>
        </defs>

        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.72)"
          mask={`url(#${maskId})`}
        />

        {/* Contorno luminoso no recorte */}
        {hole && (
          <motion.rect
            initial={false}
            animate={{
              x: hole.x,
              y: hole.y,
              width: hole.width,
              height: hole.height,
            }}
            transition={SPRING}
            rx={radius}
            ry={radius}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
            opacity="0.9"
          />
        )}
      </svg>
    </motion.div>
  );
}
